import { z } from 'zod';
import { cursorPageQuerySchema } from './pagination.js';
/**
 * Admin broadcasts: operator-composed announcements sent to every active user.
 *
 * Title and body are the one place operator copy reaches a lock screen, so the
 * API re-validates them against the notification registry's prohibited-term
 * list; the length caps here must stay at or under the registry's
 * OPERATOR_COPY_* caps.
 */
export const ADMIN_BROADCAST_STATUSES = ['SCHEDULED', 'ENQUEUING', 'COMPLETED', 'CANCELLED'];
export const ADMIN_BROADCAST_REASONS = [
    'PRODUCT_ANNOUNCEMENT',
    'MAINTENANCE_NOTICE',
    'POLICY_UPDATE',
    'INCIDENT_NOTICE',
];
export const BROADCAST_TITLE_MAX = 80;
export const BROADCAST_BODY_MAX = 200;
/**
 * An optional image on a broadcast.
 *
 * Android only, and only for announcements: the device fetches the URL itself
 * from the public bucket. Automated notifications deliberately carry no image.
 *
 * Uploads are bounded only by the app-wide multipart cap. Firebase documents
 * one megabyte as the limit for a notification image ("Images for
 * notifications are limited to 1MB in size, and otherwise are restricted by
 * built-in Android image support"), and a larger one is not rejected on send,
 * it just may not draw on the device. That is advice worth showing an
 * operator, not a rule worth blocking them with, so it is a warning threshold
 * rather than a cap.
 */
export const BROADCAST_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** Above this, Firebase no longer promises the image will render. */
export const BROADCAST_IMAGE_ADVISORY_BYTES = 1_000_000;
export const BROADCAST_IMAGE_KEY_PATTERN = /^notifications\/broadcasts\/[0-9a-f-]{36}\.(jpg|png|webp)$/;
export const adminBroadcastImageSchema = z.object({
    key: z.string(),
    /** Null when no public base URL is configured, which blocks the send. */
    url: z.string().nullable(),
});
/** Typed, not free text: the dialog asks the operator to confirm the audience. */
export const BROADCAST_CREATE_CONFIRMATION = 'SEND_TO_ALL_USERS';
export const BROADCAST_CANCEL_CONFIRMATION = 'CANCEL_BROADCAST';
export const adminBroadcastSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1).max(BROADCAST_TITLE_MAX),
    body: z.string().min(1).max(BROADCAST_BODY_MAX),
    status: z.enum(ADMIN_BROADCAST_STATUSES),
    /**
     * Why it was sent. Stored on the broadcast rather than only in the audit
     * row: an operator reading the list is asking "what have we been pushing to
     * everyone", and an answer that needs a second trip to the audit trail is
     * one nobody makes.
     */
    reason: z.enum(ADMIN_BROADCAST_REASONS),
    scheduledAt: z.string(),
    createdByUserId: z.string().uuid().nullable(),
    enqueuedUserCount: z.number().int().nonnegative(),
    totalUserCount: z.number().int().nonnegative().nullable(),
    startedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    image: adminBroadcastImageSchema.nullable(),
    createdAt: z.string(),
});
export const adminBroadcastCreateRequestSchema = z.object({
    title: z.string().trim().min(1).max(BROADCAST_TITLE_MAX),
    body: z.string().trim().min(1).max(BROADCAST_BODY_MAX),
    /** Absent means send now; present must be a future instant. */
    scheduledAt: z.string().datetime().optional(),
    reason: z.enum(ADMIN_BROADCAST_REASONS),
    confirmation: z.literal(BROADCAST_CREATE_CONFIRMATION),
    /** From a prior upload; the console never composes this itself. */
    imageKey: z.string().regex(BROADCAST_IMAGE_KEY_PATTERN).optional(),
});
export const adminBroadcastCancelRequestSchema = z.object({
    reason: z.enum(ADMIN_BROADCAST_REASONS),
    confirmation: z.literal(BROADCAST_CANCEL_CONFIRMATION),
});
export const adminBroadcastParamsSchema = z.object({ id: z.string().uuid() });
export const adminBroadcastListQuerySchema = cursorPageQuerySchema.extend({
    status: z.enum(ADMIN_BROADCAST_STATUSES).optional(),
});
export const adminBroadcastImageUploadResponseSchema = adminBroadcastImageSchema;
//# sourceMappingURL=broadcasts.js.map